/**
 * Bracket template words for the bracket control and the template editor (ADR-0049): offsets in
 * ticks, points or $ per contract for one contract, the conversion of a typed value with its
 * rounding ("$37 → 3 ticks = 0.75 pts = $37.50 (nearest tick)" on ES), which legs a template places
 * ("TP 20t", "no SL") and the risk / reward cells ("no stop", never $0). Exact: every number comes
 * from whole ticks and integer cents (`@helm/core/brackets`).
 */
import {
  enteredLabel,
  ticksToCents,
  ticksToPoints,
  type ContractFacts,
  type Converted,
  type FieldConversion,
  type OffsetUnit,
  type Rounding,
  type SizedLeg,
} from "@helm/core/brackets";
import { MINUS, formatCents } from "./format.ts";

/** Points with the tick size's decimals: NQ 20 ticks → "5.00", GC 3 ticks → "0.3", YM 7 → "7". */
export function formatPoints(ticks: number, c: ContractFacts): string {
  const step = ticksToPoints(1, c);
  const dec = step.includes(".") ? step.length - step.indexOf(".") - 1 : 0;
  const v = ticksToPoints(ticks, c);
  const [whole = "0", frac = ""] = v.split(".");
  const width = Math.max(dec, frac.length);
  return width === 0 ? whole : `${whole}.${frac.padEnd(width, "0")}`;
}

/** Short unit names for toggles and badges. */
export const UNIT_SHORT: Readonly<Record<OffsetUnit, string>> = { ticks: "t", points: "pts", dollars: "$" };

/**
 * One offset in `unit` for contract `c` ("20t", "5.00 pts", "$100"); `full` keeps the cents
 * ("$100.00"). Ticks when `c` is unknown — the caller says why $ / points are unavailable.
 */
export function offsetText(ticks: number, unit: OffsetUnit, c: ContractFacts | null, full = false): string {
  if (unit === "ticks" || c === null) return `${String(ticks)}t`;
  if (unit === "points") return `${formatPoints(ticks, c)} pts`;
  return formatCents(ticksToCents(ticks, 1, c), { trimZeroCents: !full });
}

/** A value as typed in `unit`: "$37", "−$2.50", "5.1 pts", "12t". */
export function enteredText(entered: string, unit: OffsetUnit): string {
  return enteredLabel(entered, unit).replace(/^-/, MINUS);
}

const ROUNDED: Readonly<Record<Rounding, string>> = {
  nearest: "nearest tick",
};

/**
 * What ticks a typed value sends: "20 ticks = 5.00 pts = $100.00", plus how it was rounded when
 * the value was off the tick grid ("(nearest tick)": halves away from zero — ADR-0049).
 */
export function ticksResultText(r: Converted, c: ContractFacts | null): string {
  if (!r.ok) return r.message;
  const t = r.ticks;
  const abs = Math.abs(t);
  const sign = t < 0 ? MINUS : "";
  const parts = [`${sign}${String(abs)} ${abs === 1 ? "tick" : "ticks"}`];
  if (c !== null) {
    parts.push(`${sign}${formatPoints(abs, c)} pts`);
    parts.push(`${sign}${formatCents(ticksToCents(abs, 1, c))}`);
  }
  return `${parts.join(" = ")}${r.exact ? "" : ` (${ROUNDED[r.rounding]})`}`;
}

/** "$37 → 3 ticks = 0.75 pts = $37.50 (nearest tick)" on ES, or why it cannot be sent. */
export function conversionText(conv: FieldConversion, c: ContractFacts | null): string {
  return `${enteredText(conv.entered, conv.unit)} → ${ticksResultText(conv.result, c)}`;
}

export type BracketBadge = { kind: "tp" | "sl" | "none"; text: string };

/**
 * Which exits the legs place, in `unit`: ["TP 20t", "SL 10t"], ["TP 5.00 pts", "no SL"],
 * ["no TP", "SL $50"]. Distinct leg values join with "/".
 */
export function legBadges(
  legs: readonly SizedLeg[],
  unit: OffsetUnit,
  c: ContractFacts | null,
): BracketBadge[] {
  const side = (vals: (number | null)[], name: string, kind: "tp" | "sl"): BracketBadge => {
    const set = [...new Set(vals.flatMap((v) => (v === null ? [] : [v])))];
    if (set.length === 0) return { kind: "none", text: `no ${name}` };
    const some = vals.some((v) => v === null) ? " (some legs)" : "";
    return { kind, text: `${name} ${set.map((t) => offsetText(t, unit, c)).join("/")}${some}` };
  };
  return [
    side(
      legs.map((l) => l.targetTicks),
      "TP",
      "tp",
    ),
    side(
      legs.map((l) => l.stopTicks),
      "SL",
      "sl",
    ),
  ];
}

/** "Trail 8t after +12t, step 2t" in `unit`. */
export function trailText(
  t: { triggerTicks: number; distanceTicks: number; stepTicks: number },
  unit: OffsetUnit,
  c: ContractFacts | null,
): string {
  return `Trail ${offsetText(t.distanceTicks, unit, c)} after +${offsetText(t.triggerTicks, unit, c)}, step ${offsetText(t.stepTicks, unit, c)}`;
}

/** "BE +1t after +16t" (the offset may be negative: "BE −2t after +16t") in `unit`. */
export function breakevenText(
  b: { triggerTicks: number; offsetTicks: number },
  unit: OffsetUnit,
  c: ContractFacts | null,
): string {
  const off = offsetText(Math.abs(b.offsetTicks), unit, c);
  return `BE ${b.offsetTicks < 0 ? MINUS : "+"}${off} after +${offsetText(b.triggerTicks, unit, c)}`;
}

/** The risk / reward cells: "−$60.00" / "+$120.00", or "no stop" / "no target" — never $0. */
export function riskRewardText(rr: { riskCents: number | null; rewardCents: number | null }): {
  risk: string;
  reward: string;
} {
  return {
    risk: rr.riskCents === null ? "no stop" : formatCents(-rr.riskCents),
    reward: rr.rewardCents === null ? "no target" : formatCents(rr.rewardCents, { signed: true }),
  };
}
